/** Freehand materials: smoothing, pressure, taper. */

export const BRUSHES = ['ink', 'highlighter'] as const;
export type BrushKind = (typeof BRUSHES)[number];

export const STROKE_MODES = ['sharp', 'smooth'] as const;
export type StrokeMode = (typeof STROKE_MODES)[number];

export const BRUSH_WIDTH: Record<BrushKind, number> = {
  ink: 3,
  highlighter: 24,
};

export const PEN_WIDTH_MIN = 1;
export const PEN_WIDTH_MAX = 48;

export interface PenToolStyle {
  color: string;
  width: number;
  brush: BrushKind;
  mode: StrokeMode;
}

export interface PathToolStyle {
  color: string;
  width: number;
}

export const DEFAULT_PEN_STYLE: PenToolStyle = { color: '#1f2937', width: 3, brush: 'ink', mode: 'smooth' };
export const DEFAULT_PATH_STYLE: PathToolStyle = { color: '#1f2937', width: 3 };

export function clampPenWidth(w: number): number {
  if (!Number.isFinite(w)) return 3;
  return Math.min(PEN_WIDTH_MAX, Math.max(PEN_WIDTH_MIN, w));
}

export function isBrush(v: unknown): v is BrushKind {
  return v === 'ink' || v === 'highlighter';
}

export function isStrokeMode(v: unknown): v is StrokeMode {
  return v === 'sharp' || v === 'smooth';
}

/** Width multiplier from a stylus press (0..1): never thinner than 12 % of the chosen width. */
export function pressureMul(pressure: number): number {
  const t = Number.isFinite(pressure) ? Math.min(1, Math.max(0, pressure)) : 0.5;
  return 0.12 + 0.88 * t;
}

/** Stylus only — mice report a fake 0.5 and stay a constant line. */
export function stylusMul(e: Pick<PointerEvent, 'pointerType' | 'pressure'>): number | undefined {
  if (e.pointerType !== 'pen') return undefined;
  return pressureMul(e.pressure);
}

/**
 * Turn a live gesture into stored points.
 * `light` is the old freehand simplify (module gestures).
 */
export function finishStroke(
  raw: number[],
  pressures: number[] | undefined,
  zoom: number,
  mode: StrokeMode | 'light',
): { points: number[]; pressure?: number[] } {
  let pts = raw;
  let attrs = pressures?.slice();
  if (pts.length === 2) {
    pts = [pts[0], pts[1], pts[0] + 0.01, pts[1] + 0.01];
    if (attrs?.length === 1) attrs = [attrs[0], attrs[0]];
  }
  if (attrs && attrs.length !== pts.length / 2) attrs = undefined;
  if (mode === 'sharp') return { points: pts.slice(), pressure: attrs };
  if (mode === 'smooth') {
    const sm = movingAverage(pts, attrs, 2);
    pts = sm.points;
    attrs = sm.attrs;
    return simplifyStroke(pts, attrs, 2.5 / Math.max(zoom, 1e-6));
  }
  return simplifyStroke(pts, attrs, 0.6 / Math.max(zoom, 1e-6));
}

/** Ramer–Douglas–Peucker of a flat [x, y, ...] polyline. */
export function simplifyPolyline(pts: number[], tol: number): number[] {
  return simplifyStroke(pts, undefined, tol).points;
}

export function simplifyStroke(pts: number[], attrs: number[] | undefined, tol: number): { points: number[]; pressure?: number[] } {
  const n = pts.length / 2;
  if (n <= 2) return { points: pts.slice(), pressure: attrs?.slice() };
  const keep = new Uint8Array(n);
  keep[0] = keep[n - 1] = 1;
  const stack: [number, number][] = [[0, n - 1]];
  while (stack.length) {
    const [a, b] = stack.pop()!;
    const ax = pts[a * 2],
      ay = pts[a * 2 + 1],
      bx = pts[b * 2],
      by = pts[b * 2 + 1];
    const dx = bx - ax,
      dy = by - ay;
    const len = Math.hypot(dx, dy) || 1e-12;
    let maxD = -1;
    let idx = -1;
    for (let i = a + 1; i < b; i++) {
      const d = Math.abs((pts[i * 2] - ax) * dy - (pts[i * 2 + 1] - ay) * dx) / len;
      if (d > maxD) {
        maxD = d;
        idx = i;
      }
    }
    if (maxD > tol && idx > 0) {
      keep[idx] = 1;
      stack.push([a, idx], [idx, b]);
    }
  }
  const out: number[] = [];
  const ao: number[] | undefined = attrs ? [] : undefined;
  for (let i = 0; i < n; i++) {
    if (!keep[i]) continue;
    out.push(pts[i * 2], pts[i * 2 + 1]);
    ao?.push(attrs![i]);
  }
  return { points: out, pressure: ao };
}

function movingAverage(pts: number[], attrs: number[] | undefined, radius: number): { points: number[]; attrs?: number[] } {
  const n = pts.length / 2;
  if (n <= 2) return { points: pts.slice(), attrs: attrs?.slice() };
  const out: number[] = [];
  const ao: number[] | undefined = attrs ? [] : undefined;
  for (let i = 0; i < n; i++) {
    const a = Math.max(0, i - radius);
    const b = Math.min(n - 1, i + radius);
    let x = 0,
      y = 0,
      w = 0,
      s = 0;
    for (let j = a; j <= b; j++) {
      x += pts[j * 2];
      y += pts[j * 2 + 1];
      if (attrs) w += attrs[j];
      s++;
    }
    out.push(x / s, y / s);
    ao?.push(w / s);
  }
  return { points: out, attrs: ao };
}

/** Narrower start and end, as a real nib lifting off the paper. */
export function taperWidths(pts: number[], widths: number[], min: number): number[] {
  const n = pts.length / 2;
  if (n < 3) return widths;
  const cum = [0];
  for (let i = 1; i < n; i++) cum.push(cum[i - 1] + Math.hypot(pts[i * 2] - pts[i * 2 - 2], pts[i * 2 + 1] - pts[i * 2 - 1]));
  const len = cum[n - 1];
  const maxW = Math.max(...widths);
  if (len < maxW * 2) return widths;
  const reach = Math.min(len / 3, maxW * 2.5);
  return widths.map((w, i) => {
    const t = Math.min(1, Math.min(cum[i], len - cum[i]) / reach);
    const s = t * t * (3 - 2 * t);
    return w * (min + (1 - min) * s);
  });
}

export function strokeWidths(n: number, base: number, pressure?: number[]): number[] {
  if (pressure && pressure.length === n) return pressure.map((p) => Math.max(0.01, base * p));
  return Array.from({ length: n }, () => Math.max(0.01, base));
}
